import {
  buildCreatorDedupKey,
  CAMPAIGN_DEFAULT_PLATFORM,
  CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE,
  formatCreatorHandleInput,
  isCampaignSupportedPlatform,
  type CampaignDraftCreatorItem,
} from '@/entities/campaign';
import {
  FetchHandleListFromSavedList,
  useSavedListListsQuery,
  type SavedList,
} from '@/entities/saved-creators-list';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { withFallbackCreatorRow } from '../../../../hooks/campaign-create-shared';

type SelectedCreatorListImport = {
  id: string;
  title: string;
  rowIds: string[];
};

type UseCampaignCreatorSavedListImportArgs = {
  creatorItems: CampaignDraftCreatorItem[];
  draftCreatorItems: CampaignDraftCreatorItem[];
  remainingSlots: number;
  resetCreatorValidation: () => void;
  setCreatorItems: Dispatch<SetStateAction<CampaignDraftCreatorItem[]>>;
};

export function useCampaignCreatorSavedListImport({
  creatorItems,
  draftCreatorItems,
  remainingSlots,
  resetCreatorValidation,
  setCreatorItems,
}: UseCampaignCreatorSavedListImportArgs) {
  const [selectedCreatorListImports, setSelectedCreatorListImports] = useState<
    SelectedCreatorListImport[]
  >([]);
  const savedListsQuery = useSavedListListsQuery();
  const savedLists = useMemo(() => savedListsQuery.data ?? [], [savedListsQuery.data]);

  useEffect(() => {
    const currentRowIds = new Set(creatorItems.map((item) => item.id));
    const nextSelectedCreatorListImports = selectedCreatorListImports.flatMap((list) => {
      const rowIds = list.rowIds.filter((rowId) => currentRowIds.has(rowId));
      return rowIds.length > 0 ? [{ ...list, rowIds }] : [];
    });

    const hasSameSelection =
      nextSelectedCreatorListImports.length === selectedCreatorListImports.length &&
      nextSelectedCreatorListImports.every(
        (list, index) => list.rowIds.length === selectedCreatorListImports[index]?.rowIds.length,
      );

    if (hasSameSelection) return;
    setSelectedCreatorListImports(nextSelectedCreatorListImports);
  }, [creatorItems, selectedCreatorListImports]);

  const selectedCreatorLists = useMemo(
    () =>
      selectedCreatorListImports.map((list) => ({
        id: list.id,
        title: list.title,
        importedCount: list.rowIds.length,
      })),
    [selectedCreatorListImports],
  );

  const selectedCreatorListRowIdsById = useMemo(
    () =>
      new Map(selectedCreatorListImports.map((list) => [list.id, new Set(list.rowIds)] as const)),
    [selectedCreatorListImports],
  );

  const importCreatorsFromSavedList = useCallback(
    async (list: SavedList) => {
      try {
        const handles = await FetchHandleListFromSavedList(list.id);
        const existingKeys = new Set(
          draftCreatorItems.map((item) => buildCreatorDedupKey(item)).filter(Boolean),
        );
        const nextRows: CampaignDraftCreatorItem[] = [];
        let duplicates = 0;
        let skippedByLimit = 0;
        let skippedByPlatform = 0;

        handles.creators.forEach((item) => {
          const nextItem: CampaignDraftCreatorItem = {
            id: crypto.randomUUID(),
            platform: item.platform ?? CAMPAIGN_DEFAULT_PLATFORM,
            sourceValue: formatCreatorHandleInput(item.username),
          };
          const sourceKey = buildCreatorDedupKey(nextItem);
          if (!sourceKey) return;

          if (!isCampaignSupportedPlatform(nextItem.platform)) {
            skippedByPlatform += 1;
            return;
          }

          if (existingKeys.has(sourceKey)) {
            duplicates += 1;
            return;
          }

          if (nextRows.length >= remainingSlots) {
            skippedByLimit += 1;
            return;
          }

          existingKeys.add(sourceKey);
          nextRows.push(nextItem);
        });

        if (nextRows.length > 0) {
          resetCreatorValidation();
          setCreatorItems(withFallbackCreatorRow([...draftCreatorItems, ...nextRows]));
          setSelectedCreatorListImports((currentLists) => {
            const nextRowIds = nextRows.map((row) => row.id);
            const existingList = currentLists.find((currentList) => currentList.id === list.id);

            if (!existingList) {
              return [...currentLists, { id: list.id, title: list.title, rowIds: nextRowIds }];
            }

            return currentLists.map((currentList) =>
              currentList.id === list.id
                ? {
                    ...currentList,
                    title: list.title,
                    rowIds: Array.from(new Set([...currentList.rowIds, ...nextRowIds])),
                  }
                : currentList,
            );
          });
        }
        if (nextRows.length > 0) {
          toast.success(
            `Imported ${nextRows.length} creator${nextRows.length === 1 ? '' : 's'} from ${list.title}.`,
          );
        } else if (duplicates > 0) {
          toast.message(`All creators in ${list.title} are already in this campaign.`);
        }

        if (skippedByLimit > 0) {
          toast.warning(
            `${skippedByLimit} creator${skippedByLimit === 1 ? '' : 's'} could not be added because the campaign is at its tracking limit.`,
          );
        }

        if (skippedByPlatform > 0) {
          toast.warning(CAMPAIGN_UNSUPPORTED_PLATFORM_MESSAGE);
        }
      } catch {
        toast.error(`Failed to import creators from ${list.title}.`);
      }
    },
    [
      draftCreatorItems,
      remainingSlots,
      resetCreatorValidation,
      setCreatorItems,
    ],
  );

  const clearSelectedCreatorList = useCallback(
    (listId: string) => {
      if (!listId) return;
      const rowIds = selectedCreatorListRowIdsById.get(listId);
      if (!rowIds) return;

      setCreatorItems((items) =>
        withFallbackCreatorRow(items.filter((item) => !rowIds.has(item.id))),
      );
      setSelectedCreatorListImports((currentLists) =>
        currentLists.filter((currentList) => currentList.id !== listId),
      );
    },
    [selectedCreatorListRowIdsById, setCreatorItems],
  );

  return {
    clearSelectedCreatorList,
    importCreatorsFromSavedList,
    savedLists,
    savedListsUnavailable: savedListsQuery.isError,
    selectedCreatorLists,
  };
}
